import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import {
  ADDRESS_SCHEME_TYPES,
  CONFIG_DATE_FORMAT,
  CONFIG_NAMES,
  PUNCHOUT_IN_SHOP_BEHAVIOUR_TYPES,
} from '@/libs/constants/configs'
import { SUPPORTED_LANGUAGES } from '@/libs/constants/lang'

import type { RootState } from '../store'

const fallbackDateFormat = 'YYYY-MM-DD'
const fallbackNumberSeparator = 'en-GB'
const fallbackPunchoutInShopBehaviour = PUNCHOUT_IN_SHOP_BEHAVIOUR_TYPES.FORCE_RETURN

interface ConfigState {
  availableLanguages: {
    isLoading: boolean
    isError: boolean
    data: string[] | null
  }
  availableCountries: {
    isLoading: boolean
    isError: boolean
    data: string[] | null
  }
  currencyConfig: {
    currency_position_front: boolean
    currency_symbol: string | undefined
  }
  dateFormat: string
  logo: string
  serviceRepairPage: string
  isCurrentTimeInSAPDowntime: boolean
  locales: string[]
  equipmentManagerLink: string
  hideQuickEntry: boolean
  hideHouseNumberEntryField: boolean
  regionList: string[]
  numberSeparator: string
  servicePackagePage: string
  showSparePartLink: boolean
  availableSaleOrgs: string[]
  shouldRenderServiceMenu: boolean
  countryNames: string[]
  addressScheme: string
  deactivateShoppingCart: boolean
  hideListPriceGlobal: boolean
  showRequiresInstruction: boolean
  rentalAvailable?: boolean
  hideStatus?: boolean
  showVAT?: boolean
  isLoading?: boolean
  isError?: boolean
  hideEstimatedDeliveryTime?: boolean
  isRequiredReference?: boolean
  hideVoucher?: boolean
  punchoutInShopBehaviour?: string
}

const initialState: ConfigState = {
  availableLanguages: {
    isLoading: false,
    isError: false,
    data: null,
  },
  availableCountries: {
    isLoading: false,
    isError: false,
    data: null,
  },
  currencyConfig: {
    currency_position_front: false,
    currency_symbol: '',
  },
  dateFormat: fallbackDateFormat,
  logo: 'karcher',
  serviceRepairPage: '',
  isCurrentTimeInSAPDowntime: false,
  locales: SUPPORTED_LANGUAGES,
  equipmentManagerLink: '',
  hideQuickEntry: true,
  hideHouseNumberEntryField: false,
  regionList: [],
  numberSeparator: fallbackNumberSeparator,
  servicePackagePage: '',
  showSparePartLink: false,
  availableSaleOrgs: [],
  shouldRenderServiceMenu: true,
  countryNames: [],
  addressScheme: ADDRESS_SCHEME_TYPES.STANDARD,
  deactivateShoppingCart: false,
  hideListPriceGlobal: false,
  showRequiresInstruction: true,
  hideStatus: false,
  showVAT: false,
  hideEstimatedDeliveryTime: false,
  isRequiredReference: false,
  hideVoucher: false,
  punchoutInShopBehaviour: fallbackPunchoutInShopBehaviour,
}

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    getAllConfig() {
      // empty action
    },
    getConfig(state, action: PayloadAction<keyof ConfigState>) {
      const source = state[action.payload]
      if (source && typeof source === 'object' && source !== null && 'isLoading' in source) {
        source.isLoading = true
        source.isError = false
        source.data = null
      }
    },
    getConfigSuccess(state, action: PayloadAction<{ key: keyof ConfigState; data: string[] }>) {
      const source = state[action.payload.key]
      if (source && typeof source === 'object' && source !== null && 'isLoading' in source) {
        source.isLoading = false
        source.isError = false
        source.data = action.payload.data
      }
    },
    getConfigFailed(state, action: PayloadAction<keyof ConfigState>) {
      const source = state[action.payload]
      if (source && typeof source === 'object' && source !== null && 'isLoading' in source) {
        source.isLoading = false
        source.isError = true
        source.data = null
      }
    },
    getUserConfigurationsSuccess(state, { payload }) {
      const {
        currency_position_front: currencyPositionFront,
        currency_symbol: currencySymbol,
        Logo: logo,
        date_format: dateFormat,
        Equipment_Manager_link: equipmentManagerLink,
        Hide_quick_entry: hideQuickEntry,
        Rental_Available: rentalAvailable,
        service_repair_page: serviceRepairPage,
        Address_scheme: addressScheme,
        Region_list: regionList,
        [CONFIG_NAMES.AVAILABLE_LANGUAGES]: availableLanguages,
        [CONFIG_NAMES.NUMBER_SEPARATOR]: numberSeparator,
        [CONFIG_NAMES.SERVICE_PACKAGE_PAGE]: servicePackagePage,
        [CONFIG_NAMES.SHOW_SPARE_PART_LINK]: showSparePartLink,
        [CONFIG_NAMES.SERVICE_MENU]: shouldRenderServiceMenu,
        [CONFIG_NAMES.HIDE_HOUSE_NUMBER_ENTRY_FIELD]: hideHouseNumberEntryField,
        [CONFIG_NAMES.AVAILABLE_SALESORG]: availableSaleOrgs,
        [CONFIG_NAMES.COUNTRY_NAME]: countryNames,
        [CONFIG_NAMES.DEACTIVATE_SHOPPING_CART]: deactivateShoppingCart,
        [CONFIG_NAMES.HIDE_LIST_PRICE_GLOBAL]: hideListPriceGlobal,
        [CONFIG_NAMES.SHOW_REQUIRES_INSTRUCTION]: showRequiresInstruction,
        [CONFIG_NAMES.HIDE_STATUS]: hideStatus,
        [CONFIG_NAMES.SHOW_VAT]: showVAT,
        [CONFIG_NAMES.HIDE_ESTIMATED_DELIVERY_TIME]: hideEstimatedDeliveryTime,
        [CONFIG_NAMES.IS_REQUIRED_REFERENCE]: isRequiredReference,
        [CONFIG_NAMES.HIDE_VOUCHER]: hideVoucher,
        [CONFIG_NAMES.PUNCHOUT_IN_SHOP_BEHAVIOUR]: punchoutInShopBehaviour,
      } = payload ?? {}

      state.currencyConfig.currency_position_front = currencyPositionFront
      state.currencyConfig.currency_symbol = currencySymbol
      state.logo = logo
      state.rentalAvailable = rentalAvailable
      state.shouldRenderServiceMenu = shouldRenderServiceMenu
      state.availableSaleOrgs = availableSaleOrgs
      state.countryNames = countryNames ? (JSON.parse(countryNames) as string[]) : []
      state.addressScheme = addressScheme
      state.deactivateShoppingCart = deactivateShoppingCart
      state.hideListPriceGlobal = hideListPriceGlobal
      state.hideStatus = hideStatus
      state.showVAT = showVAT
      state.hideEstimatedDeliveryTime = hideEstimatedDeliveryTime
      state.isRequiredReference = isRequiredReference
      state.hideVoucher = hideVoucher
      state.punchoutInShopBehaviour = punchoutInShopBehaviour ?? fallbackPunchoutInShopBehaviour
      if (CONFIG_DATE_FORMAT.findIndex((configItem) => configItem.value === dateFormat) !== -1) {
        state.dateFormat = dateFormat
      }
      if (addressScheme !== ADDRESS_SCHEME_TYPES.STANDARD) {
        try {
          state.regionList = JSON.parse(regionList) as string[]
        } catch {
          // TODO
        }
      }
      state.equipmentManagerLink = equipmentManagerLink
      state.hideQuickEntry = hideQuickEntry
      state.hideHouseNumberEntryField = hideHouseNumberEntryField
      state.serviceRepairPage = serviceRepairPage
      state.availableLanguages.data = availableLanguages
      state.numberSeparator = numberSeparator
      state.servicePackagePage = servicePackagePage
      state.showSparePartLink = showSparePartLink
      state.showRequiresInstruction = showRequiresInstruction
      state.isLoading = false
      state.isError = false
    },
    getUserConfigurationsFailed() {
      // empty action
    },
    getSAPDowntimeSuccess(state, action: PayloadAction<boolean>) {
      state.isCurrentTimeInSAPDowntime = action.payload
    },
    getCurrencyConfigSuccess(state, action: PayloadAction<Partial<ConfigState['currencyConfig']>>) {
      Object.assign(state.currencyConfig, action.payload)
    },
  },
})

export const {
  getAllConfig,
  getConfig,
  getConfigSuccess,
  getConfigFailed,
  getUserConfigurationsSuccess,
  getUserConfigurationsFailed,
  getSAPDowntimeSuccess,
  getCurrencyConfigSuccess,
} = configSlice.actions

export default configSlice.reducer

export const selectIsCurrentTimeInSAPDowntime = (state: RootState): boolean =>
  state.config.isCurrentTimeInSAPDowntime
